import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

/* ================= Helpers ================= */
const formatearFecha = (fechaStr) => {
  if (!fechaStr) return "—";
  const fecha = new Date(fechaStr);
  if (Number.isNaN(fecha.getTime())) return "—";
  return fecha.toLocaleDateString("es-CO", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const ListarEnteVsJefe = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [asociaciones, setAsociaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  /* ================= Eliminar ================= */
  const deleteAsociacion = useCallback(async (asociacionId) => {
    try {
      const result = await Swal.fire({
        title: "¿Está seguro?",
        text: "¡Esta acción no se puede deshacer!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (!result.isConfirmed) return;

      setDeletingId(asociacionId);

      const { data } = await axios.delete(`/api/asociarjefes/delete/${asociacionId}`);

      await Swal.fire({
        icon: "success",
        title: data?.message || "Asociación eliminada con éxito",
        showConfirmButton: false,
        timer: 1500,
      });

      // ✅ Actualización optimista
      setAsociaciones((prev) => prev.filter((a) => a._id !== asociacionId));
    } catch (error) {
      console.error("Error al eliminar la asociación:", error.message);
      Swal.fire("Error", "No se pudo eliminar la asociación.", "error");
    } finally {
      setDeletingId(null);
    }
  }, []);

  /* ================= Obtener datos (SOLO DATOS, sin JSX) ================= */
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(`/api/asociarjefes/listbyente/${id}`);
      const lista = data?.asociaciones || [];

      const formateadas = lista.map((asociacion) => {
        const jefe = asociacion.jefeObjectId || {};
        const ente = asociacion.enteObjectId || {};

        const nombreCompleto =
          [jefe.nombre1Jefe, jefe.nombre2Jefe, jefe.apellido1Jefe, jefe.apellido2Jefe]
            .filter(Boolean)
            .join(" ") || "Sin información";

        return {
          ...asociacion,
          nombreCompleto,
          documentoJefe: jefe.documentoJefe || "N/A",
          nombreEnteConformador: ente.nombreEnteconformador || "N/A",
          fechaAsignacion: formatearFecha(asociacion.fechaAsignacion),
        };
      });

      setAsociaciones(formateadas);
    } catch (error) {
      console.error("Error al cargar los datos de asociaciones:", error.message);
      setAsociaciones([]);
      Swal.fire("Error", "No se pudieron cargar las asociaciones.", "error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) getData();
  }, [id, getData]);

  /* ================= Agregar JSX de funciones fuera de getData ================= */
  const dataWithFunciones = useMemo(() => {
    return (asociaciones || []).map((asociacion) => ({
      ...asociacion,
      funciones: (
        <div className="d-flex gap-2 flex-nowrap justify-content-end align-items-center">
          {/* Editar */}
          <button
            className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
            style={{ width: 34, height: 34 }}
            onClick={() =>
              navigate(`/enteconformador/asociar/jefeinmediato/editar/${asociacion._id}`)
            }
            title="Editar"
            type="button"
            disabled={loading || deletingId === asociacion._id}
          >
            <i className="fa-solid fa-pen-to-square"></i>
          </button>

          {/* Eliminar */}
          <button
            className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
            style={{ width: 34, height: 34 }}
            onClick={() => deleteAsociacion(asociacion._id)}
            title="Eliminar"
            type="button"
            disabled={loading || deletingId === asociacion._id}
          >
            {deletingId === asociacion._id ? (
              <i className="fa-solid fa-spinner fa-spin text-white"></i>
            ) : (
              <i className="fa-solid fa-trash"></i>
            )}
          </button>
        </div>
      ),
    }));
  }, [asociaciones, loading, deletingId, navigate, deleteAsociacion]);

  /* ================= Columns ================= */
  const columns = useMemo(
    () => [
      { Header: "Jefe", accessor: "nombreCompleto" },
      { Header: "Ente Conformador", accessor: "nombreEnteConformador" },
      { Header: "Fecha Asignación", accessor: "fechaAsignacion" },
      { Header: "Estado", accessor: "estadoAsignacion" },
      { accessor: "funciones" },
    ],
    []
  );

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">Asignaciones Ente vs Jefe</h5>
                <small className="text-secondary">
                  Gestión de asignaciones: editar y eliminar.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate(-1)}
                  title="Volver"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"} me-2`}></i>
                  Actualizar
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" role="status" aria-hidden="true"></div>
                <div className="mt-3">Cargando asociaciones…</div>
              </div>
            ) : Array.isArray(dataWithFunciones) && dataWithFunciones.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={dataWithFunciones} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">🔗</div>
                <div className="fw-semibold">No hay asociaciones registradas</div>
                <div className="small">No se encontraron asignaciones para este ente.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarEnteVsJefe;
